import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { deg2rad } from '@com/utils/unit-converters/angle'
import { toQcColor } from '@ps/bridge/qc/color'
import { setEnableGlow, setItalic } from '@ps/utils/game'
import {
  isFreeDrawLineNode,
  isRestrictedNode,
  isText,
} from '@ps/bridge/qc/base'
import store from '@ps/store'
import * as editTextScript from '@ps/bridge/qc/scripts/edit-text'
import { updatePositionTween } from '@ps/workbench/animation'
import { setFont, setText } from '@ps/bridge/qc/node/text'
import { updateLanguageConfig } from '@ps/bridge/qc/scripts/language'
import { resetLayoutData, restoreLayout } from '@ps/bridge/qc/scripts/layout'
import { getMirrorNodeAttrs } from '@ps/store/workbench/game/mirror-node'
import {
  closePreviewAllBox2dShape,
  isRigidBody,
} from '@ps/workbench/undo-redo/module/box2d'
import { getDescendants } from '@ps/bridge/qc/node/utils'

export default {
  setRatioLock(state, { value, node }) {
    const activeNode = node || state.activeNode
    const ped = editorScript.getScript(activeNode)
    ped.$data.ratioLock = value
  },

  setFileSize(state, { value, node }) {
    const activeNode = node || state.activeNode
    const ped = editorScript.getScript(activeNode)
    ped.$data.size = value
  },

  setDuration(state, { value, node }) {
    const activeNode = node || state.activeNode
    const ped = editorScript.getScript(activeNode)
    ped.$data.duration = value
  },

  setWidth(state, { value, node }) {
    value = Number(parseFloat(value).toFixed(2))
    // 涉及设置宽高要先重置layoutData
    restoreLayout(node)
    // ps.Layout的hack处理,先设置psLayout.layoutData[res]不同分辨率下的宽高并且refresh才能更新新的宽高值
    this.dispatch('workspace/game/screenAdapter/setWidth', {
      width: value,
      node: node || state.activeNode,
    })
    if (
      typeof node === 'undefined' ||
      (state.activeNode && node.uuid === state.activeNode.uuid)
    ) {
      state.activeNode.width = value
      node = state.activeNode
    } else {
      node.width = value
    }
    this.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
      node,
    })
  },

  setHeight(state, { value, node }) {
    value = parseFloat(value)
    // 涉及设置宽高要先重置layoutData
    restoreLayout(node)
    // ps.Layout的hack处理,先设置psLayout.layoutData[res]不同分辨率下的宽高并且refresh才能更新新的宽高值
    this.dispatch('workspace/game/screenAdapter/setHeight', {
      height: Number(value.toFixed(2)),
      node: node || state.activeNode,
    })
    if (
      typeof node === 'undefined' ||
      (state.activeNode && node.uuid === state.activeNode.uuid)
    ) {
      state.activeNode.height = Number(value.toFixed(2))
      node = state.activeNode
    } else {
      node.height = Number(value.toFixed(2))
    }
    this.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
      node,
    })
  },

  // node.x和node.anchoredX是不一样的
  setX(state, { value, node }) {
    value = parseFloat(value)
    // 涉及设置宽高要先重置layoutData
    restoreLayout(node)
    // 如果是受限制的节点，当节点的已经到达边界时，不允许再移动
    if (isRestrictedNode(node)) {
      const minX = -(
        node.parent.width * node.parent.pivotX -
        node.width * node.pivotX
      )
      const maxX =
        node.parent.width -
        node.parent.width * node.parent.pivotX -
        node.width +
        node.width * node.pivotX
      value = Math.min(maxX, Math.max(minX, value))
    }
    if (
      typeof node === 'undefined' ||
      (state.activeNode && node.uuid === state.activeNode.uuid)
    ) {
      state.activeNode.x = Number(value.toFixed(2))
      node = state.activeNode
    } else {
      node.x = Number(value.toFixed(2))
    }
    updatePositionTween(node)
    this.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
      node,
    })
  },

  // node.y和node.anchoredY是不一样的
  setY(state, { value, node }) {
    value = parseFloat(value)
    // 涉及设置宽高要先重置layoutData
    restoreLayout(node)
    // 如果是受限制的节点，当节点的已经到达边界时，不允许再移动
    if (isRestrictedNode(node)) {
      const minY = -(
        node.parent.height * node.parent.pivotY -
        node.height * node.pivotY
      )
      const maxY =
        node.parent.height -
        node.parent.height * node.parent.pivotY -
        node.height +
        node.height * node.pivotY
      value = Math.min(maxY, Math.max(minY, value))
    }
    if (
      typeof node === 'undefined' ||
      (state.activeNode && node.uuid === state.activeNode.uuid)
    ) {
      state.activeNode.y = Number(value.toFixed(2))
      node = state.activeNode
    } else {
      node.y = Number(value.toFixed(2))
    }
    updatePositionTween(node)
    this.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
      node,
    })
  },

  setAnchoredX(state, { value, node }) {
    value = parseFloat(value)
    if (
      typeof node === 'undefined' ||
      (state.activeNode && node.uuid === state.activeNode.uuid)
    ) {
      state.activeNode.anchoredX = Number(value.toFixed(2))
      node = state.activeNode
    } else {
      node.anchoredX = Number(value.toFixed(2))
    }
    this.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
      node,
    })
  },

  setAnchoredY(state, { value, node }) {
    value = parseFloat(value)
    if (
      typeof node === 'undefined' ||
      (state.activeNode && node.uuid === state.activeNode.uuid)
    ) {
      state.activeNode.anchoredY = Number(value.toFixed(2))
      node = state.activeNode
    } else {
      node.anchoredY = Number(value.toFixed(2))
    }
    this.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
      node,
    })
  },

  setScaleX(state, { value, node }) {
    value = parseFloat(value)
    restoreLayout(node)
    if (
      typeof node === 'undefined' ||
      (state.activeNode && node.uuid === state.activeNode.uuid)
    ) {
      state.activeNode.scaleX = Number(value.toFixed(2))
      node = state.activeNode
    } else {
      node.scaleX = Number(value.toFixed(2))
    }
    this.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
      node,
    })
  },

  setScaleY(state, { value, node }) {
    value = parseFloat(value)
    restoreLayout(node)
    if (
      typeof node === 'undefined' ||
      (state.activeNode && node.uuid === state.activeNode.uuid)
    ) {
      state.activeNode.scaleY = Number(value.toFixed(2))
      node = state.activeNode
    } else {
      node.scaleY = Number(value.toFixed(2))
    }
    this.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
      node,
    })
  },

  setPivotX(state, { value, node }) {
    restoreLayout(node)
    this.commit('workspace/game/resetLayoutData', node)
    value = parseFloat(value)
    if (
      typeof node === 'undefined' ||
      (state.activeNode && node.uuid === state.activeNode.uuid)
    ) {
      state.activeNode.pivotX = Number(value.toFixed(2))
      node = state.activeNode
    } else {
      node.pivotX = Number(value.toFixed(2))
    }
    this.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
      node,
    })
  },

  setPivotY(state, { value, node }) {
    restoreLayout(node)
    this.commit('workspace/game/resetLayoutData', node)
    value = parseFloat(value)
    if (
      typeof node === 'undefined' ||
      (state.activeNode && node.uuid === state.activeNode.uuid)
    ) {
      state.activeNode.pivotY = Number(value.toFixed(2))
      node = state.activeNode
    } else {
      node.pivotY = Number(value.toFixed(2))
    }
    this.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
      node,
    })
  },

  setRotation(state, { value, node }) {
    restoreLayout(node)
    // 角度转弧度 red = deg * PI / 180
    value = parseFloat(value)
    const rad = deg2rad(value)
    if (
      typeof node === 'undefined' ||
      (state.activeNode && node.uuid === state.activeNode.uuid)
    ) {
      state.activeNode.rotation = rad
      node = state.activeNode
    } else {
      node.rotation = rad
    }
    this.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
      node,
    })
  },

  setAlpha(state, { value, node }) {
    console.log(value)
    value = parseFloat(value)
    const tempAlpha = Number(value.toFixed(2))
    if (typeof tempAlpha === 'undefined') return
    if (
      typeof node === 'undefined' ||
      (state.activeNode && node.uuid === state.activeNode.uuid)
    ) {
      state.activeNode.alpha = tempAlpha
      node = state.activeNode
    } else {
      node.alpha = tempAlpha
    }
    this.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
      node,
    })
  },

  /**
   * setLineSpacing
   * @param {{}} state
   * @param {{value: string, node: {}}} param1 node:qc.Node
   */
  setLineSpacing(state, { value, node }) {
    value = value === '' ? 0 : Number(value)
    if (node) {
      node.lineSpacing = value
    } else {
      state.activeNode.lineSpacing = value
    }
  },

  setAlignH(state, { value }) {
    state.activeNode.alignH = value
  },

  setAlignV(state, { value }) {
    state.activeNode.alignV = value
  },

  setGlowBlur(state, { value }) {
    value = value === '' ? 0 : Number(value)
    state.activeNode.glowBlur = value
  },

  setGradient(state, { value, node }) {
    const targetNode = node || state.activeNode
    targetNode.gradient = value
  },

  /**
   * setColor
   * @param {{}} state
   * @param {{value: string, node: {}}} param1 node:qc.Node
   */
  setColor(state, { value, node }) {
    const color = toQcColor(value)
    const targetNode = node || state.activeNode
    // 问题：qc.Color 的实例的原型链上无法找到 Color。qc-core-debug.js 源码中设置 activeNode.color 时会判断值的原型链有没有 Color 构造函数，所以会报错。如果没有找到更好的办法，暂时的解决方案是将判断去掉
    targetNode.color = color
    // 新增了下划线与删除线，颜色随着文本颜色变而变
    targetNode.underlineColor = color
    targetNode.deletelineColor = color
  },

  /**
   * setColorTint
   * @param {{}} state
   * @param {{value: string, node: {}}} param1 node:qc.Node
   */
  setColorTint(state, { value, node }) {
    const color = toQcColor(value)
    if (node) {
      node.colorTint = color
    } else {
      state.activeNode.colorTint = color
    }
  },

  /**
   * setStartColor
   * @param {{}} state
   * @param {{value: string, node: {}}} param1 node:qc.Node
   */
  setStartColor(state, { value, node }) {
    const color = toQcColor(value)
    if (node) {
      node.startColor = color
    } else {
      // 问题：qc.Color 的实例的原型链上无法找到 Color。qc-core-debug.js 源码中设置 activeNode.color 时会判断值的原型链有没有 Color 构造函数，所以会报错。如果没有找到更好的办法，暂时的解决方案是将判断去掉
      state.activeNode.startColor = color
    }
  },

  /**
   * setEndColor
   * @param {{}} state
   * @param {{value: string, node: {}}} param1 node:qc.Node
   */
  setEndColor(state, { value, node }) {
    const color = toQcColor(value)
    if (node) {
      node.endColor = color
    } else {
      // 问题：qc.Color 的实例的原型链上无法找到 Color。qc-core-debug.js 源码中设置 activeNode.color 时会判断值的原型链有没有 Color 构造函数，所以会报错。如果没有找到更好的办法，暂时的解决方案是将判断去掉
      state.activeNode.endColor = color
    }
  },

  setBold(state, { value, node }) {
    if (node) {
      node.bold = value
    } else {
      state.activeNode.bold = value
    }
  },

  setWrap(state, { value, node }) {
    if (node) {
      node.wrap = value
    } else {
      state.activeNode.wrap = value
    }
  },

  setStrokeThickness(state, { value, node }) {
    value = value === '' ? 0 : Number(value)
    if (node) {
      node.strokeThickness = value
    } else {
      state.activeNode.strokeThickness = value
    }
  },

  setItalic(state, { value, node }) {
    if (node) {
      setItalic(node, value)
    } else {
      setItalic(state.activeNode, value)
    }
  },

  setStroke(state, { value, node }) {
    const color = toQcColor(value)
    if (node) {
      node.stroke = color
    } else {
      state.activeNode.stroke = color
    }
  },

  setEnableGlow(state, { value, node }) {
    if (node) {
      setEnableGlow(node, value)
    } else {
      setEnableGlow(state.activeNode, value)
    }
  },

  setEnableShadow(state, { value, node }) {
    if (node) {
      node.enableShadow = value
    } else {
      state.activeNode.enableShadow = value
    }
  },

  setGlowColor(state, { value, node }) {
    const color = toQcColor(value)
    if (node) {
      node.glowColor = color
    } else {
      state.activeNode.glowColor = color
    }
  },

  setShadowColor(state, { value, node }) {
    const color = toQcColor(value)
    if (node) {
      node.shadowColor = color
    } else {
      state.activeNode.shadowColor = color
    }
  },

  setShadowBlur(state, { value, node }) {
    value = value === '' ? 0 : Number(value)
    if (node) {
      node.shadowBlur = value
    } else {
      state.activeNode.shadowBlur = value
    }
  },

  setShadowOffsetX(state, { value, node }) {
    value = value === '' ? 0 : Number(value)
    if (node) {
      node.shadowOffsetX = value
    } else {
      state.activeNode.shadowOffsetX = value
    }
  },

  setShadowOffsetY(state, { value, node }) {
    value = value === '' ? 0 : Number(value)
    if (node) {
      node.shadowOffsetY = value
    } else {
      state.activeNode.shadowOffsetY = value
    }
  },

  /**
   * setName
   * @param {{value: string, node: {}}} param1 node:qc.Node
   */
  async setName(state, { value, node }) {
    const targetNode = node || state.activeNode
    if (targetNode.name === value) return
    if (targetNode && isText(targetNode)) {
      updateLanguageConfig(targetNode, {
        name: value,
      })
    }
    if (targetNode) {
      targetNode.name = value
      // 改名时需要更新事件系统对应的动作显示UI
      await store.dispatch('workspace/game/visualProgram/updateParamUI')
    }
  },

  /**
   * setUniqueName
   * @param {{}} state
   * @param {{value: string, node: {}}} param1 node:qc.Node
   */
  setUniqueName(state, { value, node }) {
    if (node) {
      node.uniqueName = value
    } else {
      state.activeNode.uniqueName = value
    }
    // 改名时需要更新事件系统对应的动作显示UI
    store.dispatch('workspace/game/visualProgram/updateParamUI')
  },

  /**
   * setFontSize
   * @param {{}} state
   * @param {{value: string, node: {}}} param1 node:qc.Node
   */
  setFontSize(state, { value, node }) {
    value = value === '' ? 0 : Number(value)
    if (node) {
      node.fontSize = value
    } else {
      state.activeNode.fontSize = value
    }
  },

  resetLayoutData(state, node) {
    const adapterResolution =
      store.state.workspace.game.device.adapterResolution
    // 为什么要这样做，因为设置部分属性(visible/pivot) 的时候，在历史 layout 脚本里面会触发 refresh 操作，如果不 reset 的话，会导致部分属性(visible/pivot) 的值无法设置
    resetLayoutData(node, adapterResolution)
  },

  /**
   * setVisible
   * @param {{}} state
   * @param {{value: boolean, node: {}}} param1 node:qc.Node
   */
  setVisible(state, { value, node }) {
    restoreLayout(node)
    this.commit('workspace/game/resetLayoutData', node)
    if (node) {
      node.visible = value
    } else {
      state.activeNode.visible = value
    }
    // 文本节点，需要判定有没有editText脚本，有则设置其proxyVisible
    if (isText(node)) {
      const editText = editTextScript.getScript(node, false)
      if (editText) {
        editText.proxyVisible = value
      }
    }
    // 隐藏图层时要关闭刚体轮廓
    const nodes = getDescendants(node)
    for (const i in nodes) {
      if (isRigidBody(nodes[i]) && !isFreeDrawLineNode(nodes[i])) {
        store.commit('workspace/game/updatePreviewBox2dShapeState', {
          val: false,
        })
        store.commit('workspace/game/updatePreviewBox2dShapeAllState', {
          val: false,
        })
        closePreviewAllBox2dShape()
      }
    }
    store.dispatch('workspace/stage/toggleShapeVisible', {
      node,
    })
    this.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
      node,
    })
  },

  setImageType(state, { value, node }) {
    if (node) {
      node.imageType = value
    } else {
      state.activeNode.imageType = value
    }
  },

  setBorderTop(state, { value, node }) {
    if (node) {
      node.borderTop = value
    } else {
      state.activeNode.borderTop = value
    }
  },

  setBorderLeft(state, { value, node }) {
    if (node) {
      node.borderLeft = value
    } else {
      state.activeNode.borderLeft = value
    }
  },

  setBorderRight(state, { value, node }) {
    if (node) {
      node.borderRight = value
    } else {
      state.activeNode.borderRight = value
    }
  },

  setBorderBottom(state, { value, node }) {
    if (node) {
      node.borderBottom = value
    } else {
      state.activeNode.borderBottom = value
    }
  },

  setFont(state, { value, node }) {
    node = node || state.activeNode
    setFont({
      node,
      fontName: value,
    })
  },

  /**
   * setText
   * @param {{}} state
   * @param {{value: string, node: {}}} param1 node:qc.Node
   */
  setText(state, { value, node }) {
    node = node || state.activeNode
    setText({
      node,
      text: value,
    })
  },
  updateMirrorNodeAttrs(state, { node }) {
    const activeNode = node || state.activeNode
    state.mirrorNode = getMirrorNodeAttrs(activeNode)
  },
}
